import React from 'react';

import AddIcon from '@mui/icons-material/Add';

import RuleCombinatorControl from 'containers/MemberGroups/components/RuleBuilder/RuleCombinator';
import { RULE_MAX_CONDITIONS } from 'containers/MemberGroups/components/RuleBuilder/registry';
import {
  AddConditionButton,
  RuleActions,
  RuleBuilderRoot,
  RuleLimitNote,
} from 'containers/MemberGroups/components/RuleBuilder/styles';

import { RuleCombinator } from 'utils/api/modules/memberGroups';
import { FieldCondition, FieldConditionRule } from 'utils/api/modules/entities/entities.types';

import ConditionRow from './ConditionRow';
import { EventFieldCatalogue } from './eventFields';
import { newCondition } from './model';

// The Groups rule shell, with two differences: the field catalogue arrives as a prop rather than
// from a store, and a rule may drop to zero conditions, because a field is allowed to stop being
// conditional whereas a group is not allowed to stop having a rule.

export interface ConditionBuilderProps {
  rule: FieldConditionRule;
  catalogue: EventFieldCatalogue;
  /** The field being configured, which may not be its own trigger. */
  ownerKey: string;
  /** Display names for stored uuid values, accumulated as the account owner picks them. */
  labels: Record<string, string>;
  /** Rows a refused collapse has named, by position in `rule.conditions`. */
  invalidRows?: number[];
  onChange: (rule: FieldConditionRule, labels?: Record<string, string>) => void;
}

const ConditionBuilder: React.FunctionComponent<ConditionBuilderProps> = ({
  rule,
  catalogue,
  ownerKey,
  labels,
  invalidRows,
  onChange,
}) => {
  const { conditions, combinator } = rule;
  const invalid = new Set(invalidRows ?? []);

  // A field reading its own answer can never be satisfied: hidden, so unanswered, so still hidden.
  const pickerFields = catalogue.pickerFields.filter((field) => field.key !== ownerKey);
  const atLimit = conditions.length >= RULE_MAX_CONDITIONS;

  const [rowIds, setRowIds] = React.useState<number[]>(() => conditions.map((_, index) => index));
  if (rowIds.length !== conditions.length) {
    setRowIds(conditions.map((_, index) => index));
  }
  const nextId = rowIds.length === 0 ? 0 : Math.max(...rowIds) + 1;

  const updateCondition = (index: number, condition: FieldCondition, valueLabels?: Record<string, string>): void => {
    onChange({ ...rule, conditions: conditions.map((c, i) => (i === index ? condition : c)) }, valueLabels);
  };

  const removeCondition = (index: number): void => {
    setRowIds((previous) => previous.filter((_, i) => i !== index));
    onChange({ ...rule, conditions: conditions.filter((_, i) => i !== index) });
  };

  const addCondition = (): void => {
    if (atLimit) {
      return;
    }
    setRowIds((previous) => [...previous, nextId]);
    onChange({ ...rule, conditions: [...conditions, newCondition()] });
  };

  return (
    <RuleBuilderRoot>
      {conditions.map((condition, index) => (
        <React.Fragment key={rowIds[index]}>
          {index > 0 && (
            <div data-testid="condition-combinator">
              <RuleCombinatorControl
                combinator={combinator as RuleCombinator}
                interactive={index === 1}
                onChange={(next) => onChange({ ...rule, combinator: next })}
              />
            </div>
          )}
          <ConditionRow
            condition={condition}
            index={index}
            pickerFields={pickerFields}
            catalogue={catalogue}
            labels={labels}
            invalid={invalid.has(index)}
            onChange={(next, valueLabels) => updateCondition(index, next, valueLabels)}
            onRemove={() => removeCondition(index)}
          />
        </React.Fragment>
      ))}

      <RuleActions>
        {atLimit ? (
          <RuleLimitNote>You can add up to {RULE_MAX_CONDITIONS} conditions.</RuleLimitNote>
        ) : (
          <AddConditionButton type="button" data-testid="condition-add" onClick={addCondition}>
            <AddIcon fontSize="small" />
            Add condition
          </AddConditionButton>
        )}
      </RuleActions>
    </RuleBuilderRoot>
  );
};

export default ConditionBuilder;
